import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Loader2, Check, Clock, ArrowRight, BookOpen, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* --- Sub-components --- */

const PreviewStep = ({ product, onJoinWaitlist, onClose }) => (
  <div className="space-y-5 pt-2">
    <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(163,184,153,0.25)" }}>
        <BookOpen size={18} style={{ color: "var(--ll-green)" }} />
      </div>
      <div>
        <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{product.name}</p>
        <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Here&apos;s what&apos;s inside your 160+ page workbook</p>
      </div>
    </div>
    <div className="space-y-2.5">
      {product.preview_sections.map((section, i) => (
        <div key={section.title} className="flex gap-3 p-3 rounded-xl transition-all"
          style={{ background: i % 2 === 0 ? "white" : "var(--ll-bg-secondary)", border: "1px solid var(--ll-border)" }}>
          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: "var(--ll-green)", color: "white", fontSize: "10px", fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>{i + 1}</div>
          <div>
            <p className="text-sm font-medium mb-0.5" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{section.title}</p>
            <p className="text-[11px] leading-relaxed" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{section.description}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between pt-2">
      <div>
        <span className="font-serif text-lg font-semibold" style={{ color: "var(--ll-text)" }}>R{product.launch_price_zar || product.price_zar}</span>
        {product.launch_price_zar && product.price_zar > product.launch_price_zar && (
          <span className="text-xs line-through ml-1.5" style={{ color: "var(--ll-text-muted)" }}>R{product.price_zar}</span>
        )}
        <span className="text-[10px] tracking-wider uppercase ml-2 px-2 py-0.5 rounded-full"
          style={{ background: "rgba(247,208,70,0.15)", color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>Launch Price</span>
      </div>
    </div>
    <button data-testid="waitlist-preview-cta" onClick={onJoinWaitlist}
      className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
      style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
      <Bell size={16} /> Join Waitlist — Be First to Know <ArrowRight size={14} />
    </button>
    <button onClick={onClose} className="w-full text-xs text-center py-1 transition-colors hover:opacity-70"
      style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Maybe later</button>
  </div>
);

const FormStep = ({ product, name, setName, email, setEmail, loading, onSubmit, hasPreview, onShowPreview }) => (
  <form onSubmit={onSubmit} className="space-y-5 pt-2">
    <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(247,208,70,0.2)" }}>
        <Clock size={18} style={{ color: "var(--ll-accent-hover)" }} />
      </div>
      <div>
        <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{product.name}</p>
        <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Coming soon — be the first to know when it launches</p>
      </div>
    </div>
    {!hasPreview && product.features && (
      <ul className="space-y-1.5">
        {product.features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-xs" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            <Check size={12} style={{ color: "var(--ll-green)" }} /> {f}
          </li>
        ))}
      </ul>
    )}
    <div className="space-y-2">
      <Label htmlFor="waitlist-name" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Name</Label>
      <Input id="waitlist-name" data-testid="waitlist-name-input" placeholder="Your name" value={name}
        onChange={(e) => setName(e.target.value)} className="rounded-xl border bg-white/50 focus:bg-white" style={{ borderColor: "var(--ll-border)" }} />
    </div>
    <div className="space-y-2">
      <Label htmlFor="waitlist-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
      <Input id="waitlist-email" data-testid="waitlist-email-input" type="email" placeholder="you@example.com" required value={email}
        onChange={(e) => setEmail(e.target.value)} className="rounded-xl border bg-white/50 focus:bg-white" style={{ borderColor: "var(--ll-border)" }} />
    </div>
    <button type="submit" data-testid="waitlist-submit-btn" disabled={loading}
      className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
      style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
      {loading ? "Joining..." : "Notify Me When Available"}
    </button>
    <p className="text-xs text-center" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
      We&apos;ll only email you when this product launches. No spam.
    </p>
    {hasPreview && (
      <button type="button" onClick={onShowPreview}
        className="w-full text-xs text-center py-1 flex items-center justify-center gap-1 transition-colors hover:opacity-70"
        style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        <Eye size={11} /> View sneak peek again
      </button>
    )}
  </form>
);

const SuccessStep = ({ product, onClose }) => (
  <div className="py-6 space-y-5 text-center">
    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--ll-green)" }}>
      <Check size={28} style={{ color: "white" }} />
    </div>
    <div>
      <p className="text-sm mb-1" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>You&apos;ll be the first to know when</p>
      <p className="font-serif text-lg font-medium" style={{ color: "var(--ll-text)" }}>{product.name}</p>
      <p className="text-sm mt-1" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>becomes available.</p>
    </div>
    <button data-testid="waitlist-close-btn" onClick={onClose}
      className="rounded-full px-6 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5"
      style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Continue Browsing</button>
  </div>
);

/* --- Main Modal --- */

const WaitlistModal = ({ open, onClose, product }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("preview");

  if (!product) return null;
  const hasPreview = product.preview_sections && product.preview_sections.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email address."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist/join`, { product_id: product.id, email, name });
      setStep("success");
      toast.success("You're on the waitlist!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setStep(hasPreview ? "preview" : "form"); setEmail(""); setName(""); onClose(); };

  const stepTitle = step === "preview" ? "Sneak Peek" : step === "form" ? "Join the Waitlist" : "You\u2019re on the List!";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="waitlist-modal" className="sm:max-w-lg rounded-2xl border max-h-[90vh] overflow-y-auto"
        style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg)" }}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium" style={{ color: "var(--ll-text)" }}>{stepTitle}</DialogTitle>
          <DialogDescription className="sr-only">Preview and waitlist for {product.name}</DialogDescription>
        </DialogHeader>

        {step === "preview" && hasPreview && <PreviewStep product={product} onJoinWaitlist={() => setStep("form")} onClose={handleClose} />}
        {step === "form" && <FormStep product={product} name={name} setName={setName} email={email} setEmail={setEmail} loading={loading} onSubmit={handleSubmit} hasPreview={hasPreview} onShowPreview={() => setStep("preview")} />}
        {step === "success" && <SuccessStep product={product} onClose={handleClose} />}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
